"use client";

import { useState } from "react";
import { DeviceSelect } from "./device-select";
import { MediaToggleButton } from "./media-toggle-button";
import { ReadyToJoinCard } from "./ready-to-join-card";
import { MobileHeader } from "../../components/mobile-header";
import { DesktopHeader } from "../../components/desktop-header";

type UserPreferencesProps = {
  onReady: () => void;
};

// Mock devices
const mockMicrophones = [
  { id: "mic-1", label: "MacBook Microphone (Built-in)" },
  { id: "mic-2", label: "External Microphone" },
  { id: "mic-3", label: "AirPods Pro" },
];

const mockSpeakers = [
  { id: "speaker-1", label: "Macbook Dynamics (Built-In)" },
  { id: "speaker-2", label: "External Speakers" },
  { id: "speaker-3", label: "AirPods Pro" },
];

const mockCameras = [
  { id: "cam-1", label: "Camera MacBook (Built-In)" },
  { id: "cam-2", label: "External Webcam" },
];

export function UserPreferences({ onReady }: UserPreferencesProps) {
  const [isMicOn, setIsMicOn] = useState(true);
  const [isCameraOn, setIsCameraOn] = useState(true);
  const [selectedMic, setSelectedMic] = useState("mic-1");
  const [selectedSpeaker, setSelectedSpeaker] = useState("speaker-1");
  const [selectedCamera, setSelectedCamera] = useState("cam-1");

  return (
    <div className="flex flex-col w-full min-h-screen bg-[#f3e4c9]">
      <DesktopHeader
        returnPath="/training/overview"
        title="Audio/Video Setup"
      />
      <MobileHeader returnPath="/training/overview" />

      {/* Desktop Main Content */}
      <div className="hidden md:flex flex-col gap-[20px] items-center px-5 py-10 w-full">
        {/* Video and Device Selectors Card */}
        <div className="bg-white border border-[#e3d8c4] border-solid flex flex-col gap-[24px] items-start p-[20px] rounded-[32px] shadow-[0px_4px_4px_0px_rgba(41,37,36,0.12)] max-w-full xl:max-w-[960px] w-full">
          {/* Video Preview with Overlay Controls */}
          <div className="relative flex flex-col aspect-video w-full items-center justify-center px-[32px] py-[40px] rounded-[20px] bg-[#292524] overflow-hidden">
            {/* Video placeholder */}
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-center">
                <p className="text-[#797268] text-lg font-semibold">
                  Camera is off
                </p>
              </div>
            </div>

            {/* Bottom Controls on Video */}
            <div className="absolute bottom-[20px] left-1/2 transform -translate-x-1/2 flex gap-[16px]">
              <MediaToggleButton
                type="audio"
                enabled={isMicOn}
                onToggle={() => setIsMicOn(!isMicOn)}
                size="lg"
              />
              <MediaToggleButton
                type="video"
                enabled={isCameraOn}
                onToggle={() => setIsCameraOn(!isCameraOn)}
                size="lg"
              />
            </div>
          </div>

          {/* Device Selectors Row */}
          <div className="flex gap-[16px] items-center justify-center w-full overflow-hidden">
            <DeviceSelect
              type="audioinput"
              value={selectedMic}
              devices={mockMicrophones}
              onValueChange={setSelectedMic}
              className="flex-1 min-w-0 max-w-[280px]"
            />
            <DeviceSelect
              type="audiooutput"
              value={selectedSpeaker}
              devices={mockSpeakers}
              onValueChange={setSelectedSpeaker}
              className="flex-1 min-w-0 max-w-[280px]"
            />
            <DeviceSelect
              type="videoinput"
              value={selectedCamera}
              devices={mockCameras}
              onValueChange={setSelectedCamera}
              className="flex-1 min-w-0 max-w-[280px]"
            />
          </div>
        </div>

        {/* Ready to Join Card - Desktop */}
        <ReadyToJoinCard onReady={onReady} variant="desktop" />
      </div>

      {/* Mobile Main Content */}
      <div className="md:hidden flex flex-col flex-auto gap-[24px] items-center pt-[146px] pb-[220px]">
        <p className="font-semibold leading-[32px] text-[#292524] text-[24px] text-center">
          Audio/Video Setup
        </p>

        {/* Vertical Video Card */}
        <div className="relative border border-[#e3d8c4] border-solid flex flex-col flex-auto aspect-[9/16] items-center pb-[16px] pt-[12px] px-[12px] rounded-[20px] shadow-[0px_4px_4px_0px_rgba(41,37,36,0.12)] bg-white overflow-hidden">
          {/* Video background */}
          <div className="absolute inset-0 bg-[#292524] rounded-[20px]">
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-center">
                <p className="text-[#797268] text-lg font-semibold">
                  Camera is off
                </p>
              </div>
            </div>
          </div>

          {/* Bottom Controls */}
          <div className="absolute bottom-[15.5px] left-1/2 transform -translate-x-1/2 flex gap-[12px]">
            <MediaToggleButton
              type="audio"
              enabled={isMicOn}
              onToggle={() => setIsMicOn(!isMicOn)}
              size="lg"
            />
            <MediaToggleButton
              type="video"
              enabled={isCameraOn}
              onToggle={() => setIsCameraOn(!isCameraOn)}
              size="lg"
            />
          </div>
        </div>
      </div>

      {/* Mobile Bottom Card - Fixed */}
      <ReadyToJoinCard onReady={onReady} variant="mobile" />
    </div>
  );
}
